import React, { createContext, useState, useContext, useCallback } from 'react';
import { ciService, sourceService } from '../services/api';

// Collective Intelligence search state — kept separate from SearchContext so
// the existing personal-KB search flow is untouched. PoC sidebar only drives
// the universal scope; the context can be extended with team/group scopes
// later without changing the consumer API.

export const CISearchContext = createContext(null);

export const useCISearch = () => {
  const context = useContext(CISearchContext);
  if (context === undefined) {
    throw new Error('useCISearch must be used within a CISearchProvider');
  }
  return context;
};

const initialState = {
  query: '',
  results: null,
  isSearching: false,
  error: null,
  currentPage: 1,
  scopeType: 'universal',
  scopeId: null,
  resultSources: [],      // sources derived from
                          // current search results
  selectedSources: [],    // domains currently filtered
                          // out of displayed results
  selectedEntries: new Set()  // focusEntryIds checked for bulk claim.
                              // Lives here rather than in the component so
                              // that every path which replaces the result set
                              // clears it — see executeSearch below.
};

export const CISearchProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  // Derive the source list from current search results.
  // Called after every successful search to populate
  // the result facet. Counts are hits in the result
  // set, not total corpus counts.
  const deriveResultSources = useCallback(async (
    entries
  ) => {
    if (!entries || entries.length === 0) {
      setState(prev => ({
        ...prev,
        resultSources: [],
        selectedSources: []
      }));
      return;
    }

    const domainCounts = new Map();
    for (const entry of entries) {
      if (!entry.document?.docUrl) continue;
      try {
        const domain = new URL(entry.document.docUrl)
          .hostname.replace(/^www\./, '');
        if (domain) {
          domainCounts.set(
            domain,
            (domainCounts.get(domain) || 0) + 1
          );
        }
      } catch {
        // malformed URL — skip
      }
    }

    const domains = Array.from(domainCounts.keys());

    // Resolve display names via shared endpoint
    let displayNames = [];
    try {
      displayNames = await sourceService.resolveDomains(
        domains
      );
    } catch {
      // Fall back to raw domains if resolution fails
      displayNames = domains.map(d => ({
        domain: d,
        displayName: d
      }));
    }

    const nameMap = new Map(
      displayNames.map(s => [s.domain, s.displayName])
    );

    const sources = Array.from(domainCounts.entries())
      .map(([domain, count]) => ({
        domain,
        displayName: nameMap.get(domain) || domain,
        count
      }))
      .sort((a, b) => b.count - a.count);

    setState(prev => ({
      ...prev,
      resultSources: sources,
      selectedSources: []
    }));
  }, []);

  const executeSearch = useCallback(async (
    query,
    page = 1,
    scopeType = 'universal',
    scopeId = null,
    sourceDomains = null
  ) => {
    if (!query || !query.trim()) return;
    setState(prev => ({
      ...prev,
      isSearching: true,
      error: null,
      query,
      scopeType,
      scopeId,
      currentPage: page,
      // Clear checkbox selection whenever the result set is about to be
      // replaced. Deliberately placed HERE, in executeSearch's unconditional
      // opening setState, rather than in deriveResultSources where
      // selectedSources is cleared: deriveResultSources runs only when
      // `!sourceDomains` (see below), a guard that exists so unchecking a
      // source does not wipe the source list. That guard is right for
      // selectedSources and wrong for selectedEntries — it would exempt the
      // source-filter toggle, which is precisely a trigger that replaces the
      // visible rows. All three triggers (new search, page change,
      // source-filter toggle) funnel through executeSearch, so clearing here
      // covers all of them, and any future trigger that does the same.
      selectedEntries: new Set()
    }));
    try {
      const response = await ciService.search(
        query.trim(),
        page,
        scopeType,
        scopeId,
        sourceDomains
      );
      setState(prev => ({
        ...prev,
        results: response,
        currentPage: page,
        isSearching: false
      }));
      // Only rebuild the source list on fresh searches.
      // When sourceDomains is set, the user is toggling a
      // source filter — preserve the existing source list
      // so unchecked sources don't disappear.
      if (!sourceDomains) {
        deriveResultSources(response.entries || []);
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.error || error.message || 'CI search failed',
        isSearching: false
      }));
    }
  }, [deriveResultSources]);

  const clearSearch = useCallback(() => {
    // Fresh Set rather than initialState's: initialState is a module-level
    // object, so reusing its Set would share one instance across every reset.
    // Nothing mutates it in place today (toggleSelection is copy-on-write),
    // but a shared mutable default is a trap worth not leaving behind.
    setState({ ...initialState, selectedEntries: new Set() });
  }, []);

  // Toggle a source domain in/out of the excluded set.
  // Triggers a re-search with the updated exclusion list.
  const toggleResultSource = useCallback((domain) => {
    setState(prev => {
      const current = prev.selectedSources;
      const updated = current.includes(domain)
        ? current.filter(d => d !== domain)
        : [...current, domain];
      return { ...prev, selectedSources: updated };
    });
  }, []);

  const setPage = useCallback(async (newPage) => {
    if (state.query) {
      return executeSearch(
        state.query,
        newPage,
        state.scopeType,
        state.scopeId,
        null
      );
    }
    return null;
  }, [
    state.query,
    state.scopeType,
    state.scopeId,
    executeSearch
  ]);

  // Toggle one focusEntryId in/out of the bulk-claim selection.
  // Copy-on-write so React sees a new Set reference and re-renders.
  const toggleSelection = useCallback((focusEntryId) => {
    setState(prev => {
      const next = new Set(prev.selectedEntries);
      if (next.has(focusEntryId)) {
        next.delete(focusEntryId);
      } else {
        next.add(focusEntryId);
      }
      return { ...prev, selectedEntries: next };
    });
  }, []);

  // Drop specific ids from the selection, or clear it entirely when called
  // with no argument. handleBulkClaim uses the targeted form so that ids which
  // FAILED to claim stay selected and can be retried, while claimed and
  // already-claimed ids leave the selection immediately — without waiting for
  // the next fetch-triggered clear, which may not come for some time.
  const clearSelection = useCallback((focusEntryIds = null) => {
    setState(prev => {
      if (!focusEntryIds) {
        if (prev.selectedEntries.size === 0) return prev;
        return { ...prev, selectedEntries: new Set() };
      }
      const next = new Set(prev.selectedEntries);
      for (const id of focusEntryIds) next.delete(id);
      return { ...prev, selectedEntries: next };
    });
  }, []);

  // Optimistically remove a claimed focus from the visible result set.
  //
  // RETAINED DELIBERATELY, UNCALLED (2026-09-14). The claim flow now greys
  // claimed rows in place rather than removing them, so nothing calls this
  // today. It is kept exported because removal remains a legitimate future
  // behaviour for this result set, and re-deriving it later is pure cost.
  const removeEntry = useCallback((focusEntryId) => {
    setState(prev => {
      if (!prev.results) return prev;
      const filtered = prev.results.entries.filter(e => e.focusEntryId !== focusEntryId);
      return {
        ...prev,
        results: {
          ...prev.results,
          entries: filtered,
          total: Math.max(0, (prev.results.total || filtered.length) - 1),
          userExcludedCount: (prev.results.userExcludedCount || 0) + 1
        }
      };
    });
  }, []);

  return (
    <CISearchContext.Provider value={{
      ...state,
      executeSearch,
      clearSearch,
      setPage,
      removeEntry,
      deriveResultSources,
      toggleResultSource,
      toggleSelection,
      clearSelection
    }}>
      {children}
    </CISearchContext.Provider>
  );
};
